import { useLanguage } from "@/i18n/LanguageContext";
import { Link } from "react-router-dom";
import { usePublishedIntegrationPages } from "@/lib/integrationPages";
import LongPage from "@/components/LongPage";
import {
  IntegrationMapDiagram,
  FlowTable,
  ProcessTrack,
  DiagramFrame,
} from "@/components/platform/Diagrams";

export default function IntegrationsPage() {
  const { t, language, localizedPath } = useLanguage();
  const pages = usePublishedIntegrationPages(language);
  const pagesSection = pages.length ? [{
    id: "connected-systems", kind: "prose" as const, eyebrow: "Connected systems", heading: "Integration guides",
    body: (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {pages.map((p) => (
          <Link key={p.id} to={localizedPath(`/integrations/${p.slug}`)} className="rounded-lg border border-border bg-card p-4 hover:bg-accent">
            {p.logo_url && <img src={p.logo_url} alt="" className="h-8 w-auto mb-2" />}
            <div className="text-sm font-semibold">{p.title}</div>
            {p.excerpt && <p className="text-sm text-muted-foreground mt-1">{p.excerpt}</p>}
          </Link>
        ))}
      </div>
    ),
  }] : [];
  return (
    <LongPage
      eyebrow={t("Integrations")}
      title={t("Connect planning, production and analysis without replacing your core systems")}
      description={t("BEEDIGIT connects business systems with the real shop floor. Production plans, master data and job orders move into execution, while verified output, consumption, downtime, quality, maintenance and energy facts flow back to ERP, BI and other operational systems.")}
      seoTitle="Manufacturing Integrations: ERP, CMMS, QMS, BI | BEEDIGIT"
      seoDescription="Connected, not replaced. See what data flows into BEEDIGIT from ERP and what returns: output, scrap, consumption, downtime, quality, maintenance and energy facts."
      bullets={[
        "Connected, not replaced — your ERP stays the system of record",
        "Defined business objects, directions and owners per project",
        "Real-time, scheduled, event-based or file-based exchange",
        "Direct shop-floor collection from modern and legacy equipment",
      ]}
      primaryCta={{ label: "Discuss an Integration", to: "/get-started" }}
      secondaryCta={{ label: "See the data flow", to: "#integration-map" }}
      heroVisual={<IntegrationMapDiagram />}
      ctaHeading="Let's map your production data flow"
      ctaBody="Show us your ERP, production process and required confirmations. We will help define what should flow into BEEDIGIT, what should return to your systems and where automation creates the most value."
      ctaSecondary={{ label: "See How BEEDIGIT Works", to: "/platform" }}
      sections={[
        ...pagesSection,
        {
          id: "integration-map",
          kind: "prose",
          eyebrow: "Landscape",
          heading: "One production flow, connected systems",
          body: (
            <div className="space-y-6">
              <p className="text-muted-foreground max-w-3xl">
                {t("Every system in a factory already has a job. The gap is usually between the plan and physical execution — that is the part BEEDIGIT fills.")}
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  { t: "ERP / APS", d: "Defines what should be produced and when." },
                  { t: "BEEDIGIT", d: "Turns the production plan into shop-floor execution and contextualises actual events." },
                  { t: "Machines and operators", d: "Generate the real production facts." },
                  { t: "CMMS and QMS", d: "Use maintenance and quality events connected to the production context." },
                  { t: "BI and data platforms", d: "Combine manufacturing data with financial and business analytics." },
                ].map((r) => (
                  <div key={r.t} className="rounded-lg border border-border bg-card p-4">
                    <div className="text-sm font-semibold mb-1">{t(r.t)}</div>
                    <p className="text-sm text-muted-foreground">{t(r.d)}</p>
                  </div>
                ))}
              </div>
              <DiagramFrame
                label={t("The planning to execution gap: ERP and APS decide what to produce, physical machines and operators produce it, and BEEDIGIT connects the two by dispatching work and returning contextualised facts.")}
                caption={t("Without this layer, the plan and the physical result are reconciled manually, hours or days later.")}
              >
                <div className="grid md:grid-cols-[1fr_auto_1fr] gap-3 items-center">
                  <div className="rounded-lg border border-border bg-[hsl(var(--section-alt))] p-4">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">{t("Planning")}</div>
                    <div className="text-sm font-semibold">{t("What should be produced")}</div>
                    <p className="text-xs text-muted-foreground mt-1">{t("ERP · APS · master data")}</p>
                  </div>
                  <div className="rounded-lg border border-primary/50 bg-primary/10 p-4 text-center">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">{t("The gap")}</div>
                    <div className="text-sm font-semibold">{t("BEEDIGIT")}</div>
                    <p className="text-xs text-muted-foreground mt-1">{t("dispatch · capture · context · confirm")}</p>
                  </div>
                  <div className="rounded-lg border border-border bg-[hsl(var(--section-alt))] p-4">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">{t("Execution")}</div>
                    <div className="text-sm font-semibold">{t("What was actually produced")}</div>
                    <p className="text-xs text-muted-foreground mt-1">{t("machines · operators · materials")}</p>
                  </div>
                </div>
              </DiagramFrame>
            </div>
          ),
        },
        {
          id: "categories",
          kind: "prose",
          eyebrow: "Categories",
          heading: "Four integration categories",
          body: (
            <div className="space-y-4">
              {[
                {
                  t: "ERP and planning",
                  inTitle: "Data into BEEDIGIT",
                  in: ["Production orders", "Products and master data", "BOMs and routings", "Planned quantities and dates", "Work centers and calendars"],
                  outTitle: "Data returned",
                  out: ["Actual output", "Scrap and rejected quantities", "Material consumption", "Order progress", "Start and finish times", "Production status"],
                },
                {
                  t: "Maintenance and quality",
                  inTitle: "Typically into BEEDIGIT",
                  in: ["Equipment master data", "Inspection plans", "Batch and order context"],
                  outTitle: "Typically out of BEEDIGIT",
                  out: ["Maintenance requests", "Downtime and failure events", "Quality results", "Non-conformances"],
                },
                {
                  t: "BI and data platforms",
                  inTitle: "Usually read-only",
                  in: ["No write-back required", "Refresh on schedule or on event"],
                  outTitle: "Possible outputs",
                  out: ["Production KPIs", "OEE components", "Downtime events", "Energy information", "Quality results", "Maintenance information", "Order, machine, shift, product and batch dimensions"],
                },
                {
                  t: "Shop-floor and OT",
                  inTitle: "Connected equipment",
                  in: ["PLCs", "Sensors", "Meters", "Industrial gateways", "Operator terminals", "Scanners and printers"],
                  outTitle: "Coverage",
                  out: ["Modern controllers via their available interfaces", "Legacy machines via added sensors and gateways, without replacing the controller", "Manual operator input where no signal exists"],
                },
              ].map((c) => (
                <div key={c.t} className="rounded-lg border border-border bg-card p-5">
                  <h3 className="font-semibold uppercase tracking-wider text-sm mb-4">{t(c.t)}</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {[{ h: c.inTitle, l: c.in }, { h: c.outTitle, l: c.out }].map((col) => (
                      <div key={col.h} className="rounded-md border border-border bg-background p-4">
                        <div className="text-[11px] font-mono uppercase tracking-wider text-primary mb-2">{t(col.h)}</div>
                        <ul className="space-y-1">
                          {col.l.map((x) => (
                            <li key={x} className="text-sm text-muted-foreground">{t(x)}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ),
        },
        {
          id: "data-flow",
          kind: "prose",
          eyebrow: "Data flow",
          heading: "What flows in, what flows out",
          body: (
            <div className="space-y-6">
              <p className="text-muted-foreground max-w-3xl">
                {t("A concrete view of the business objects usually agreed in an integration scope. The exact list depends on your systems and the confirmations you need.")}
              </p>
              <FlowTable
                rows={[
                  { object: "Production order", direction: "ERP → BEEDIGIT", source: "ERP / APS", destination: "BEEDIGIT execution", purpose: "Dispatch the job to the correct work center and operator." },
                  { object: "Actual production", direction: "BEEDIGIT → ERP", source: "Machines and operators", destination: "ERP", purpose: "Plan-versus-actual confirmation without manual re-keying." },
                  { object: "Downtime event", direction: "BEEDIGIT → CMMS / BI", source: "Machine states and operator reasons", destination: "CMMS, BI", purpose: "Maintenance response and structured loss analysis." },
                  { object: "Material consumption", direction: "BEEDIGIT → ERP", source: "Scans and confirmations", destination: "ERP", purpose: "Inventory and costing confirmation against the order." },
                  { object: "Quality result", direction: "BEEDIGIT → QMS / ERP", source: "Inspections and checks", destination: "QMS, ERP", purpose: "Release, rejection and traceability by batch." },
                  { object: "Energy data", direction: "BEEDIGIT → BI / EnMS", source: "Meters and gateways", destination: "BI, energy management", purpose: "Energy analysis by machine, order or product." },
                ]}
              />
            </div>
          ),
        },
        {
          id: "patterns",
          kind: "features",
          eyebrow: "Patterns",
          heading: "Integration patterns and when they fit",
          intro:
            "Not every pattern is available for every system — the choice depends on the interfaces your systems actually expose.",
          cols: 2,
          items: [
            { title: "Real-time API exchange", desc: "Useful when order status or confirmations must be visible immediately. Typically REST with JSON over HTTPS, where the counterpart system offers an API." },
            { title: "Scheduled or batch synchronisation", desc: "Useful for master data and end-of-shift confirmations, or when the ERP team prefers a controlled window instead of continuous traffic." },
            { title: "Event-based messaging", desc: "Useful for shop-floor signals and state changes that occur continuously, where a message is published as the event happens." },
            { title: "File exchange", desc: "Useful as a pragmatic starting point with older or regional systems — CSV drops on an agreed schedule and location." },
            { title: "Direct shop-floor data collection", desc: "Useful when machine data is not available from any business system: signals are captured at the machine through gateways and sensors." },
            { title: "Read-only analytical export", desc: "Useful for BI and data platforms that only need to consume production, quality and energy data without writing anything back." },
          ],
        },
        {
          id: "systems",
          kind: "prose",
          eyebrow: "Systems",
          heading: "Supported systems",
          body: (
            <div className="space-y-6">
              <p className="text-muted-foreground max-w-3xl">
                {t("BEEDIGIT integrates through the interfaces a system exposes. We describe this as integration experience and project-specific connectors — not as certified or one-click integrations.")}
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { t: "SAP", d: "Integration experience through available interfaces; scope and method confirmed with your SAP team." },
                  { t: "Oracle", d: "API-based integration where the relevant Oracle modules expose the required services." },
                  { t: "Microsoft Dynamics", d: "API-based integration for orders and confirmations, supported through available interfaces." },
                  { t: "Odoo", d: "API-based integration for orders, products and confirmations." },
                  { t: "Regional or custom ERP", d: "Project-specific connector using the system's API or an agreed file exchange." },
                  { t: "BI and data platforms", d: "Read-only export of production, quality, energy and maintenance data for reporting tools." },
                  { t: "CMMS and QMS", d: "Project-specific exchange of equipment data, maintenance requests, inspections and results." },
                  { t: "Machines and gateways", d: "Direct collection from controllers, sensors and meters via industrial gateways." },
                ].map((s) => (
                  <div key={s.t} className="rounded-lg border border-border bg-card p-4">
                    <div className="text-sm font-semibold mb-1">{t(s.t)}</div>
                    <p className="text-sm text-muted-foreground">{t(s.d)}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground border-t border-border pt-3 max-w-3xl">
                {t("Naming a system here indicates integration experience and available interfaces. It does not imply an official partnership, certification or a ready-made native connector.")}
              </p>
            </div>
          ),
        },
        {
          id: "scenarios",
          kind: "useCases",
          eyebrow: "Scenarios",
          heading: "Practical integration scenarios",
          intro: "What the exchange changes in daily operation.",
          items: [
            { persona: "Import production orders", scenario: "Orders and planned quantities are read from ERP instead of being typed into a shop-floor spreadsheet.", outcome: "Operators always run the current plan; no divergence between office and line." },
            { persona: "Dispatch to work centers", scenario: "Each order is routed to the correct work center, terminal and operator with its instructions.", outcome: "Less waiting and fewer wrong setups at changeover." },
            { persona: "Return output and consumption", scenario: "Confirmed good quantity, scrap and consumed material are posted back to ERP.", outcome: "Inventory and costing reflect reality without end-of-shift re-keying." },
            { persona: "Downtime to maintenance", scenario: "A stop with a technical reason creates a maintenance request carrying machine and time context.", outcome: "Maintenance responds to a described fault, not a phone call." },
            { persona: "Quality to batch", scenario: "Inspection results and non-conformances are attached to the order and batch that produced them.", outcome: "Traceable release decisions and faster complaint investigation." },
            { persona: "Production and energy to BI", scenario: "KPIs, downtime and energy data are exported into the existing BI environment.", outcome: "Management analyses production alongside financial data in one place." },
            { persona: "Master data across plants", scenario: "Products, work centers and reason codes are synchronised from one owning system.", outcome: "Plants can be compared because they use the same definitions." },
            { persona: "Connect legacy equipment", scenario: "Older machines are instrumented with sensors and a gateway instead of replacing the controller.", outcome: "Old and new equipment appear in the same OEE and downtime reporting." },
          ],
        },
        {
          id: "project",
          kind: "prose",
          eyebrow: "Delivery",
          heading: "How an integration project works",
          body: (
            <div className="space-y-6">
              <p className="text-muted-foreground max-w-3xl">
                {t("The exact scope depends on your systems, the interfaces they expose and the workflow you need. The sequence, however, is consistent.")}
              </p>
              <ProcessTrack
                steps={[
                  { n: 1, title: "Define systems and ownership", body: "Agree which system owns which object." },
                  { n: 2, title: "Identify objects and directions", body: "List what must move, and which way." },
                  { n: 3, title: "Map identifiers and fields", body: "Align order, product and work-center keys." },
                  { n: 4, title: "Choose the exchange", body: "Real-time, scheduled, event-based or file." },
                  { n: 5, title: "Validate on one flow", body: "Prove it on a single work center first." },
                  { n: 6, title: "Monitor and expand", body: "Reconcile, watch the sync, then widen scope." },
                ]}
              />
            </div>
          ),
        },
        {
          id: "control",
          kind: "features",
          eyebrow: "For IT and OT",
          heading: "Reliability and control",
          intro:
            "These points are defined during integration design and documented before go-live, so both teams know exactly how the exchange behaves.",
          cols: 2,
          items: [
            { title: "Source of truth per object", desc: "Each business object has one owning system; the other side consumes it rather than editing it." },
            { title: "Read and write boundaries", desc: "What BEEDIGIT may write back — and what it may only read — is agreed explicitly per object." },
            { title: "Data validation", desc: "Rules for mandatory fields, identifier formats and plausible quantities are defined for each interface." },
            { title: "Failed-message handling", desc: "How a rejected or unprocessed message is retried, escalated and re-sent is agreed during design." },
            { title: "Duplicate prevention", desc: "Stable identifiers per order, confirmation and event keep a repeated transfer from being counted twice." },
            { title: "Synchronisation monitoring", desc: "Who watches the exchange, and how a stalled interface becomes visible, is part of the handover." },
            { title: "Event and change history", desc: "Confirmations, downtime, quality results and material movements are stored as time-stamped events for later review." },
            { title: "Roles and permissions", desc: "Access to confirmations and integration configuration is limited by role." },
            { title: "Data reconciliation", desc: "An agreed periodic comparison between ERP figures and BEEDIGIT records keeps both sides trusted." },
          ],
        },
        {
          id: "faq",
          kind: "faq",
          eyebrow: "FAQ",
          heading: "Integration questions",
          items: [
            { q: "Does BEEDIGIT replace our ERP?", a: "No. Your ERP remains the system of record for planning, inventory and finance. BEEDIGIT executes the plan it receives and returns verified production facts." },
            { q: "Can BEEDIGIT connect to a custom or regional ERP?", a: "In most cases yes, through a project-specific connector using the system's API or an agreed file exchange. The feasibility is confirmed once we see which interfaces the system exposes." },
            { q: "Is real-time synchronisation always required?", a: "No. Real time is valuable for order status and confirmations, but master data and some confirmations work well on a schedule. The pattern is chosen per object." },
            { q: "Can we start with CSV or scheduled exchange?", a: "Yes. A file or scheduled exchange is a common and pragmatic first step, especially when API access needs internal approval. It can be replaced later without changing the shop-floor setup." },
            { q: "How are duplicate or failed records handled?", a: "Each transferred object carries a stable identifier so a repeated transfer is recognised rather than counted twice. Retry and escalation behaviour for failed messages is defined during integration design." },
            { q: "Can BEEDIGIT work with legacy machines?", a: "Yes. Where a controller offers no usable interface, signals are captured with added sensors and an industrial gateway, so old equipment reports into the same OEE and downtime model as new equipment." },
            { q: "Can an integration begin with one production line?", a: "Yes, and that is the recommended approach: validate the exchange on a single work center or line, reconcile the numbers, then extend the same pattern." },
            { q: "Who owns the master data?", a: "Master data normally stays owned by the ERP and is consumed by BEEDIGIT. Shop-floor specific data — reason codes, checks, machine configuration — is usually owned in BEEDIGIT. Ownership is agreed object by object." },
            { q: "How long does an integration project take?", a: "It depends on the number of objects, the interfaces available and the availability of your ERP team. A first scope — orders in, confirmations out on one line — is deliberately kept small so it can be validated before scaling." },
          ],
        },
      ]}
    />
  );
}